import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import moment from "moment-timezone";

import textFieldStyles from "components/Styled/TextField/styles";
import {
  TimezoneOption,
  filterTimezoneOptions,
  findTimezoneOption,
  getTimezoneOptions,
} from "utils/timezones";

interface TimezoneAutocompleteProps {
  value?: string | null;
  onChange: (event: React.SyntheticEvent, value: string | null) => void;
  label?: string;
  required?: boolean;
  renderInput?: (params: any) => React.ReactNode;
  [key: string]: any;
}

export default function TimezoneAutocomplete({
  value,
  onChange,
  label,
  required = false,
  renderInput,
  ...rest
}: TimezoneAutocompleteProps) {
  const options = getTimezoneOptions();

  return (
    <>
      {label && (
        <Typography gutterBottom>
          {label} {required ? "*" : ""}
        </Typography>
      )}
      <Autocomplete
        {...rest}
        options={options}
        value={findTimezoneOption(value)}
        onChange={(event, newValue) =>
          onChange(event, (newValue as TimezoneOption | null)?.value ?? null)
        }
        groupBy={(option) => (option as TimezoneOption).group}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.label
        }
        isOptionEqualToValue={(option, selected) =>
          (option as TimezoneOption).value ===
          (selected as TimezoneOption).value
        }
        filterOptions={(opts, state) =>
          filterTimezoneOptions(opts as TimezoneOption[], state.inputValue)
        }
        renderOption={(props, option) => {
          const opt = option as TimezoneOption;
          return (
            <Box
              component="li"
              {...props}
              key={opt.value}
              sx={{ display: "flex", gap: 2 }}
            >
              <Box component="span" sx={{ flexGrow: 1, minWidth: 0 }}>
                {opt.label}
              </Box>
              <Typography
                component="span"
                sx={{
                  color: "text.secondary",
                  fontSize: 12,
                  whiteSpace: "nowrap",
                }}
              >
                {moment.tz(opt.value).format("h:mm A")}
              </Typography>
            </Box>
          );
        }}
        renderInput={
          renderInput ||
          ((params) => (
            <TextField
              {...params}
              sx={textFieldStyles.root}
              placeholder="Search by city, region, or GMT offset"
            />
          ))
        }
      />
    </>
  );
}
